import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { APIError, UserBody, UserUpdateState } from 'models';
import * as services from 'services';
import type { RootState } from 'store/store';

export const defaultState: UserUpdateState = {
  update: {
    isSuccess: false,
    isLoading: false,
    isError: false,
    error: null,
  },
  status: {
    isSuccess: false,
    isLoading: false,
    isError: false,
    error: null,
  },
};

// Export slice
export const userUpdateSlice = createSlice({
  name: 'user-update',
  initialState: defaultState,
  reducers: {
    updateUser: (state) => {
      state.update.isSuccess = false;
      state.update.isLoading = true;
      state.update.isError = false;
      state.update.error = null;
    },
    updateUserSuccess: (state) => {
      state.update.isSuccess = true;
      state.update.isLoading = false;
    },
    updateUserFailure: (state, action: PayloadAction<APIError>) => {
      state.update.isLoading = false;
      state.update.isError = true;
      state.update.error = action.payload;
    },
    updateUserStatus: (state) => {
      state.status.isSuccess = false;
      state.status.isLoading = true;
      state.status.isError = false;
      state.status.error = null;
    },
    updateUserStatusSuccess: (state) => {
      state.status.isSuccess = true;
      state.status.isLoading = false;
    },
    updateUserStatusFailure: (state, action: PayloadAction<APIError>) => {
      state.status.isLoading = false;
      state.status.isError = true;
      state.status.error = action.payload;
    },
    resetUserUpdateState: (state) => {
      state.update = defaultState.update;
      state.status = defaultState.status;
    },
  },
});

// Export selectors
export const userUpdateSelector = (state: RootState) => state.userUpdate.update;
export const userUpdateStatusSelector = (state: RootState) =>
  state.userUpdate.status;

// Export actions
export const {
  updateUser,
  updateUserSuccess,
  updateUserFailure,
  updateUserStatus,
  updateUserStatusSuccess,
  updateUserStatusFailure,
  resetUserUpdateState,
} = userUpdateSlice.actions;

// Export reducer
export const userUpdateReducer = userUpdateSlice.reducer;

// Export thunk
export function updateUserById(userId: string, body: UserBody) {
  return async (dispatch: Dispatch) => {
    dispatch(updateUser());
    try {
      await services.updateUser(userId, body);
      dispatch(updateUserSuccess());
    } catch (e: any) {
      dispatch(updateUserFailure(e));
    }
  };
}

export function updateUserStatusById(userId: string, activate: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(updateUserStatus());
    try {
      if (activate) {
        await services.activateUser(userId);
      } else {
        await services.disableUser(userId);
      }
      dispatch(updateUserStatusSuccess());
    } catch (e: any) {
      dispatch(updateUserStatusFailure(e));
    }
  };
}
